import Typography from '@mui/material/Typography'
import React from 'react'

import { cannot } from '@/app/helpers'
import WhatsappActionsMessage from '@/app/module/common/whatsapp/convo/whatsapp-actions-message'
import WhatsappAudio from '@/app/module/common/whatsapp/convo/whatsapp-audio'
import WhatsappButtons from '@/app/module/common/whatsapp/convo/whatsapp-buttons'
import WhatsappDocument from '@/app/module/common/whatsapp/convo/whatsapp-document'
import WhatsappFlow from '@/app/module/common/whatsapp/convo/whatsapp-flow'
import WhatsappImage from '@/app/module/common/whatsapp/convo/whatsapp-image'
import WhatsappList from '@/app/module/common/whatsapp/convo/whatsapp-list'
import WhatsappLocation from '@/app/module/common/whatsapp/convo/whatsapp-location'
import WhatsappVideo from '@/app/module/common/whatsapp/convo/whatsapp-video'
import { extractWhatsappContentFromPart } from '@/app/module/common/whatsapp/whatsapp-content'
import { TGetFileFunction } from '@/app/module/conversations/types'
import { downloadFile } from '@/app/service/util/download'
import { WhatsAppLog } from '@/app/types/log'

type Props = {
  part: WhatsAppLog
  getFile: TGetFileFunction
}

const WhatsappConversationPart: React.FC<Props> = ({ part, getFile }) => {
  const [listOpen, setListOpen] = React.useState(false)
  const [downloading, setDownloading] = React.useState(false)

  const messageContent = React.useMemo(() => extractWhatsappContentFromPart(part), [part])

  const downloadAttachment = React.useCallback(
    async (url: string, filename: string) => {
      if (downloading) {
        return
      }

      setDownloading(true)
      try {
        await downloadFile(url, filename, true)
      } finally {
        setDownloading(false)
      }
    },
    [downloading],
  )
  const handleListClose = React.useCallback(() => setListOpen(false), [])

  const renderMessage = (message: string) => <Typography style={{ whiteSpace: 'pre-line' }}>{message}</Typography>

  const renderContent = () => {
    const { type, content } = messageContent
    switch (type) {
      case 'message': {
        return renderMessage(content.message)
      }

      case 'image': {
        return <WhatsappImage files={content.files} getFile={getFile} message={content.message} />
      }

      case 'audio': {
        return (
          <WhatsappAudio audioUrl={content.files?.[0]} message={content.message} getFile={getFile} partId={part.id} />
        )
      }

      case 'document': {
        return (
          <>
            {content.files.length &&
              content.files.map((document: string, index: number) => (
                <WhatsappDocument
                  key={index}
                  document={document}
                  onClick={async () => {
                    const res = await getFile(document)
                    await downloadAttachment(res.url, document)
                  }}
                />
              ))}
            {renderMessage(content.message)}
          </>
        )
      }

      case 'video': {
        return <WhatsappVideo videoFile={content.files?.[0]} message={content.message} getFile={getFile} />
      }

      case 'buttons': {
        return <WhatsappButtons content={content} />
      }

      case 'list': {
        return (
          <WhatsappList
            content={content}
            listOpen={listOpen}
            onOpen={() => setListOpen(true)}
            onClose={handleListClose}
          />
        )
      }

      case 'location': {
        return <WhatsappLocation location={content} />
      }

      case 'actions': {
        return <WhatsappActionsMessage content={content} />
      }

      case 'flow': {
        return <WhatsappFlow content={content} />
      }

      default:
        return cannot(type)
    }
  }

  const hasImage = messageContent.type === 'image' && messageContent.content.files?.length

  return (
    <div
      style={{
        ...(hasImage && {
          width: '100%',
          textAlign: 'left',
          minHeight: '200px',
        }),
      }}
    >
      {renderContent()}
    </div>
  )
}

export default WhatsappConversationPart
